<template>
  <el-row v-loading.fullscreen.lock="fullScreenLoading">
    <el-col
      :sm="{ span: 14, offset: 5 }"
      :md="{ span: 12, offset: 6 }"
      :lg="{ span: 12, offset: 6 }"
    >
      <el-header
        height="140px"
        class="app-header pt-50 pb-30"
      >
        <Menu />
        <h3 class="app-header-title mt-0">
          {{ $t("organization.create-organization") }}
        </h3>
      </el-header>
      <el-main>
        <el-form
          :model="organization"
          :rules="getFormRules"
          ref="editOrganizationForm"
          label-position="top"
        >
          <el-form-item
            :label="$t('organization.name')"
            prop="name"
          >
            <el-input v-model="organization.name" />
          </el-form-item>
          <el-form-item
            :label="$t('organization.resp-contact')"
            prop="representativeId"
          >
            <el-select
              v-model="organization.representativeId"
              filterable
            >
              <el-option
                v-for="user in userList"
                :key="user.sub"
                :label="`${user.firstName} ${user.surName} (${user.phone})`"
                :value="user.sub"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            :label="$t('organization.type-it-system.title')"
            prop="typeOfITSystem"
          >
            <el-select v-model="organization.typeOfITSystem">
              <el-option
                :label="$t('organization.type-it-system.options.plastic-card')"
                value="Plastic card"
              />
              <el-option
                :label="$t('organization.type-it-system.options.stickers')"
                value="Stickers"
              />
              <el-option
                :label="$t('organization.type-it-system.options.generated')"
                value="Generated"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            :label="$t('organization.type-sharing-tips.title')"
            prop="sharingType"
          >
            <el-select v-model="organization.sharingType">
              <el-option
                :label="$t('organization.type-sharing-tips.options.individual')"
                value="Individual"
              />
              <el-option
                :label="$t('organization.type-sharing-tips.options.pooling')"
                value="Pooling"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            :label="$t('organization.industry.title')"
            prop="industry"
            v-if="isAdmin"
          >
            <el-select v-model="organization.industry">
              <el-option
                :label="$t('organization.industry.options.barber')"
                value="Friseur"
              />
              <el-option
                :label="$t('organization.industry.options.hotel')"
                value="Hotel"
              />
              <el-option
                :label="$t('organization.industry.options.restaurant')"
                value="Restaurant"
              />
              <el-option
                :label="$t('organization.industry.options.charity')"
                value="Charity"
              />
              <el-option
                :label="$t('organization.industry.options.other')"
                value="Other"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="isAdmin"
            :label="$t('organization.platform-fee')"
            prop="fee"
          >
            <el-input-number
              :controls="false"
              v-model="organization.fee"
              :min="0"
              :step="0.1"
              :precision="2"
              :max="100"
              @change="changeFee"
            />
          </el-form-item>
          <el-form-item
            v-if="isAdmin"
            :label="$t('organization.trial-date')"
            prop="trialDate"
          >
            <el-date-picker
              v-model="organization.trialPeriodEnd"
              type="date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
            />
          </el-form-item>
          <div class="input-group">
            <h2 class="input-group-title">
              {{ $t("organization.post-address") }}
            </h2>
            <el-form-item
              :label="$t('organization.business-name')"
              prop="legalBusinessName"
              class="input-group-item"
            >
              <el-input v-model="organization.legalBusinessName" />
            </el-form-item>
            <el-form-item
              :label="$t('profile.address')"
              prop="addressStreet"
              class="input-group-item"
            >
              <el-input v-model="organization.addressStreet" />
            </el-form-item>
            <el-form-item
              :label="$t('profile.postal_code')"
              prop="addressZip"
              class="input-group-item"
            >
              <div class="el-input">
                <the-mask
                  class="el-input__inner"
                  :mask="'#####'"
                  v-model="organization.addressZip"
                />
              </div>
            </el-form-item>
            <el-form-item
              :label="$t('profile.city')"
              prop="addressCity"
              class="input-group-item"
            >
              <el-input v-model="organization.addressCity" />
            </el-form-item>
            <el-form-item
              :label="$t('profile.country')"
              prop="addressCountry"
              class="input-group-item"
            >
              <el-select
                v-model="organization.addressCountry"
                filterable
              >
                <el-option
                  v-for="(country, code) in countries"
                  :key="code"
                  :label="country"
                  :value="code"
                />
              </el-select>
            </el-form-item>
          </div>
          <el-form-item
            :label="$t('organization.send-payment-email')"
            prop="sendPaymentEmail"
          >
            <el-radio-group v-model="organization.sendEmailDisabled">
              <el-radio :label="false">
                {{ $t("yes") }}
              </el-radio>
              <el-radio :label="true">
                {{ $t("no") }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-if="isAdmin"
            :label="$t('organization.qr-code-ranges')"
            prop="qrCodeRanges"
          >
            <qr-code-ranges-repeater
              :is-admin="isAdmin"
              prop="qrCodeRanges"
              :fields="organization.qrCodeRanges"
            />
          </el-form-item>
          <el-form-item
            v-show="isAdmin"
            :label="$t('opera.payment-methods')"
            prop="enabledPaymentMethods"
          >
            <template v-for="(method, key) in availablePaymentMethods">
              <el-checkbox
                :key="key"
                :value="key"
                v-model="organization.enabledPaymentMethods"
                :label="method"
              />
            </template>
          </el-form-item>
          <el-form-item
            v-show="isAdmin"
            :label="
              $t('team.paypal-threshold', {
                threshold: organization.payPalConfig.threshold / 100,
              })
            "
            prop="payPalConfig"
          >
            <el-radio-group
              v-model="organization.payPalConfig.thresholdEnabled"
            >
              <el-radio :label="true">
                {{ $t("yes") }}
              </el-radio>
              <el-radio :label="false">
                {{ $t("no") }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-show="isAdmin && !organization.waiterPaysFee"
            :label="$t('fee.tippie-pays')"
            prop="tippiePays"
          >
            <el-radio-group v-model="organization.tippiePaysFee">
              <el-radio :label="true">
                {{ $t("yes") }}
              </el-radio>
              <el-radio :label="false">
                {{ $t("no") }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-show="isAdmin"
            :label="$t('fee.organization-pays')"
            prop="waiterPaysFee"
          >
            <el-radio-group v-model="organization.waiterPaysFee">
              <el-radio :label="true">
                {{ $t("yes") }}
              </el-radio>
              <el-radio :label="false">
                {{ $t("no") }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-show="isAdmin"
            :label="$t('presentation.toggle')"
            prop="presentationMode"
          >
            <el-radio-group v-model="organization.presentationMode">
              <el-radio :label="true">
                {{ $t("yes") }}
              </el-radio>
              <el-radio :label="false">
                {{ $t("no") }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-show="isAdmin"
            :label="$t('new-design.payments')"
            prop="newDesignPayment"
          >
            <el-radio-group v-model="organization.newDesignPayment">
              <el-radio :label="true">
                {{ $t("yes") }}
              </el-radio>
              <el-radio :label="false">
                {{ $t("no") }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item
            v-show="isAdmin"
            :label="$t('new-design.admin')"
            prop="newDesignAdmin"
          >
            <el-radio-group v-model="organization.newDesignAdmin">
              <el-radio :label="true">
                {{ $t("yes") }}
              </el-radio>
              <el-radio :label="false">
                {{ $t("no") }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item class="mt-20">
            <el-row>
              <el-col :span="11">
                <router-link
                  :to="{ name: 'Organizations' }"
                  custom
                  v-slot="{ navigate }"
                >
                  <el-button
                    class="cancel-button"
                    @click="navigate"
                  >
                    {{ $t("form.cancel") }}
                  </el-button>
                </router-link>
              </el-col>
              <el-col
                :offset="2"
                :span="11"
              >
                <el-button
                  class="primary-button"
                  type="primary"
                  @click="submitForm('editOrganizationForm')"
                >
                  {{ $t("form.save") }}
                </el-button>
              </el-col>
            </el-row>
          </el-form-item>
        </el-form>
      </el-main>
    </el-col>
  </el-row>
</template>

<script>
import { Auth } from "@aws-amplify/auth";
import Menu from "@/views/Menu/Menu";
import authHelpers from "@/plugins/auth-helpers";
import QrCodeRangesRepeater from "../Components/Repeaters/QrCodeRangesRepeater";
import countriesList from "../../assets/countries.json";

export default {
  name: "CreateOrganization",
  components: {
    Menu,
    QrCodeRangesRepeater,
  },
  data: function() {
    return {
      availablePaymentMethods: {
        ApplePay: "Apple Pay",
        GooglePay: "Google Pay",
        CreditCard: "Credit Card",
        PayPal: "PayPal",
        Klarna: "Klarna",
        ApplePayNoda: "Noda Apple Pay",
        CreditCardNoda: "Noda Credit Card",
        OpenBankNoda: "Noda Open Bank",
      },
      methodsMapping: {
        "Apple Pay": "ApplePay",
        "Google Pay": "GooglePay",
        "Credit Card": "CreditCard",
        PayPal: "PayPal",
        Klarna: "Klarna",
        "Noda Apple Pay": "ApplePayNoda",
        "Noda Credit Card": "CreditCardNoda",
        "Noda Open Bank": "OpenBankNoda",
      },
      isAdmin: false,
      fullScreenLoading: true,
      organization: {
        representativeId: null,
        payPalConfig: {
          thresholdEnabled: false,
          threshold: 500,
        },
        enabledPaymentMethods: [
          "Apple Pay",
          "Google Pay",
          "Credit Card",
          "PayPal",
          "Klarna",
        ],
        sendEmailDisabled: false,
        tippiePaysFee: false,
        waiterPaysFee: false,
        qrCodeRanges: [
          {
            qrCodeStart: undefined,
            qrRangeSize: undefined,
          },
        ],
        legalBusinessName: "",
        presentationMode: false,
        addressCountry: "",
        addressCity: "",
        addressStreet: "",
        addressZip: "",
        newDesignPayment: false,
        newDesignAdmin: false,
        fee: 6.5,
        feePercentage: 650,
        typeOfITSystem: "Plastic card",
        sharingType: "Individual",
        industry: "Friseur",
        trialPeriodEnd: new Date(
          new Date().getTime() + 24 * 60 * 60 * 1000
        ).toLocaleDateString("en-CA"),
      },
      userList: [],
    };
  },

  async created() {
    Auth.currentAuthenticatedUser().then(() => {
      authHelpers
        .isCurrentUserAdmin()
        .then((response) => {
          this.isAdmin = response;
        })
        .catch(() => {
          this.fullScreenLoading = false;
        });
    });

    await this.getUsersFromGroup("business", this.userList);
    await this.getUsersFromGroup("admin", this.userList);

    const defaultUser = this.userList.find(
      (el) => el.phone === "+491752430515"
    );

    if (defaultUser) {
      this.organization.representativeId = defaultUser.sub;
    }

    this.fullScreenLoading = false;
  },
  watch: {
    "organization.waiterPaysFee"(newVal) {
      if (newVal) {
        this.organization.tippiePaysFee = false;
      }
    },
  },
  computed: {
    getFormRules: function() {
      const qrCodeRangesValidator = (rule, value, callback) => {
        let qrCodeStartValid = true;
        let qrRangeSizeValid = true;
        value.map((el) => {
          if (!el.qrCodeStart) {
            qrCodeStartValid = true;
          }
          if (!el.qrRangeSize) {
            qrRangeSizeValid = false;
          }
        });
        if (!qrCodeStartValid) {
          callback(new Error(this.$t("form.validation.all-items-populated")));
        }
        if (!qrRangeSizeValid) {
          callback(new Error(this.$t("form.validation.all-items-populated")));
        }
        callback();
      };
      return {
        name: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("organization.name"),
            }),
            trigger: "blur",
          },
        ],
        typeOfITSystem: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("organization.type-it-system.title"),
            }),
            trigger: "change",
          },
        ],
        industry: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("organization.industry.title"),
            }),
            trigger: "change",
          },
        ],
        sharingType: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("organization.type-sharing-tips.title"),
            }),
            trigger: "change",
          },
        ],
        fee: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("organization.platform-fee"),
            }),
            trigger: "blur",
          },
        ],
        qrCodeRanges: [{ validator: qrCodeRangesValidator }],
      };
    },
    countries: () => {
      return countriesList;
    },
  },
  methods: {
    submitForm(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.createOrganization();
        } else {
          return false;
        }
      });
    },
    createOrganization() {
      this.fullscreenLoading = true;
      let organizationObject = Object.assign({}, this.organization);
      organizationObject.enabledPaymentMethods = organizationObject.enabledPaymentMethods.map(
        (method) => {
          return this.methodsMapping[method];
        }
      );
      this.$adminApi
        .post("/api/v1/entities", organizationObject)
        .then((response) => {
          this.fullscreenLoading = false;
          if (response.data.status === "Success") {
            this.$message.success(
              this.$t("form.item-created", {
                item: this.$t("organization.title"),
              })
            );
            this.$router.push({ name: "Organizations" });
          } else {
            this.$message.warning(response.data.status);
          }
        })
        .catch(() => {
          this.fullscreenLoading = false;
          this.$message.error(
            this.$t("form.item-not-created", {
              field: this.$t("organization.title"),
            })
          );
        });
    },
    changeFee(val) {
      this.organization.feePercentage = parseFloat(val) * 100;
    },
  },
};
</script>
